import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Building, Plus, Users, LogOut } from 'lucide-react';

// I.173 E0: sem "entrar numa empresa" por aqui — a ligação com empresa vem só por convite (ou pelo master do sistema);
// o banco recusa a troca de empresa do próprio perfil
export const CompanySelector = () => {
  const { createCompany, user, signOut } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [loading, setLoading] = useState(false);

  // I.176: o card "Criar Nova Empresa" segue a chave do cadastro público (a mesma da aba Cadastrar);
  // enquanto lê, ou se a leitura falhar, vale fechado
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    supabase.rpc('is_public_signup_open').then(({ data, error }) => {
      if (active) setSignupOpen(!error && data === true);
    });
    return () => { active = false; };
  }, []);

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe o nome da empresa",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await createCompany(newCompanyName.trim());
      
      if (error) {
        toast({
          title: "Erro ao criar empresa",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Empresa criada com sucesso!",
          description: "Você agora é o administrador master desta empresa.",
        });
      }
    } catch (error) {
      console.error('Error creating company:', error);
    } finally {
      setLoading(false);
    }
  };

  // 🚨 SEGURANÇA: Listagem de empresas removida por questões de segurança
  // Usuários devem ser convidados através do sistema de convites
  const searchCompanies = async () => {
    toast({
      title: "Funcionalidade desabilitada",
      description: "Por segurança, não é possível procurar empresas. Solicite um convite ao administrador.",
      variant: "destructive",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <Building className="mx-auto h-12 w-12 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Selecionar Empresa</h1>
          <p className="text-muted-foreground">
            Você precisa estar associado a uma empresa para continuar
          </p>
        </div>

        {/* Informações do usuário logado */}
        <Card className="bg-muted/50 border-dashed">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Conta atual:</p>
                <p className="font-medium text-sm truncate">{user?.email}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={signOut}
                className="flex-shrink-0"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </CardContent>
        </Card>

        {!showCreateForm && (
          <div className="space-y-4">
            {signupOpen === null ? (
              <p className="text-sm text-muted-foreground text-center py-6">Carregando...</p>
            ) : signupOpen === false ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground text-center">
                    O cadastro de novas empresas está fechado no momento.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="text-center">
                  <CardTitle className="flex items-center justify-center gap-2">
                    <Plus className="h-5 w-5" />
                    Criar Nova Empresa
                  </CardTitle>
                  <CardDescription>
                    Crie uma nova empresa e torne-se o administrador master
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full"
                  >
                    Criar Empresa
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Users className="h-5 w-5" />
                  Precisa de Acesso?
                </CardTitle>
                <CardDescription>
                  Solicite um convite ao administrador da empresa
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground text-center">
                  Por questões de segurança, você deve receber um convite por email para se juntar a uma empresa existente.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {showCreateForm && (
          <Card>
            <CardHeader>
              <CardTitle>Criar Nova Empresa</CardTitle>
              <CardDescription>
                Digite o nome da sua empresa abaixo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company-name">Nome da Empresa</Label>
                <Input
                  id="company-name"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="Digite o nome da empresa..."
                  className="mt-2"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateCompany}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Criando...' : 'Criar Empresa'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                  disabled={loading}
                >
                  Voltar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};